import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { compose } from 'react-apollo';
import { Button, TypographyStyle, TypographyWeight } from '@treelab/panda';

import { InputRadio } from '../../Input';
import { Typography } from '../../Typography';
import { Modal } from '../';
import { IRootState, IDispatch } from '../../../modules/stores';
import PrintJS from '../../../printing';

type IConnectProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
interface IProps extends IConnectProps, WrappedComponentProps {
  onClose: () => void;
}

const ModalPrintSettings: React.FC<IProps> = ({
  onClose,
  isPortrait,
  currentRecordId,
  canEdit,
  setLoadPrinting,
  updatePortraitMode,
  intl: { formatMessage },
}) => {
  const [isCurrentRecord, setIsCurrentRecord] = useState(true);
  const handlePrint = useCallback(() => {
    setLoadPrinting(true);
    PrintJS({
      config: {
        isCurrentRecord,
      },
      printable: 'capture',
      type: 'html',
      honorMarginPadding: false,
      targetStyles: ['*'],
      style: `span, th, img, .panda-pill-jar { page-break-inside: avoid; }@page { size: ${
        isPortrait ? 'portrait' : 'landscape'
      }; margin: 0 !important; padding: 0 !important; }`,
    });
  }, [setLoadPrinting, isPortrait, isCurrentRecord]);

  return (
    <Modal onClose={onClose} style={{ height: 'fit-content', width: '350px' }}>
      <>
        <Typography
          type={TypographyStyle.H3}
          weight={TypographyWeight.Medium}
          style={{ marginBottom: '16px' }}
        >
          {formatMessage({ id: 'controls.print' })}
        </Typography>
        <InputRadio
          input={{
            onChange: (value) => setIsCurrentRecord(value === 'current'),
            name: 'print-option',
            value: isCurrentRecord ? 'current' : 'all-records',
          }}
          meta={{}}
          title={formatMessage({ id: 'print.record.prompt' })}
          isSmall
          options={[
            { name: formatMessage({ id: 'print.record.current' }), value: 'current' },
            { name: formatMessage({ id: 'print.record.all' }), value: 'all-records' },
          ]}
          disabledIndexes={currentRecordId ? [] : [1]}
        />
        {canEdit && (
          <InputRadio
            input={{
              onChange: (value) => updatePortraitMode(value === 'portrait'),
              name: 'portrait-landscape',
              value: isPortrait ? 'portrait' : 'landscape',
            }}
            meta={{}}
            title={formatMessage({ id: 'print.orientation.prompt' })}
            isSmall
            options={[
              { name: formatMessage({ id: 'global.orientation.portrait' }), value: 'portrait' },
              { name: formatMessage({ id: 'global.orientation.landscape' }), value: 'landscape' },
            ]}
          />
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
          <Button
            colorTheme="blue"
            buttonType="outline"
            onClick={onClose}
            style={{ marginRight: '8px' }}
          >
            {formatMessage({ id: 'global.button.cancel' })}
          </Button>
          <Button
            onClick={() => {
              handlePrint();
              onClose();
            }}
          >
            {formatMessage({ id: 'global.button.done' })}
          </Button>
        </div>
      </>
    </Modal>
  );
};

const mapState = ({ space, core }: IRootState) => ({
  isPortrait: space.isPortrait,
  currentRecordId: core.currentRecordId,
  canEdit: core.canEdit,
});

const mapDispatch = ({ space }: IDispatch) => ({
  setLoadPrinting: space.setLoadPrinting,
  updatePortraitMode: space.updatePortraitMode,
  togglePrinting: () => space.togglePrinting(),
});

export default compose(
  injectIntl,
  connect(
    mapState,
    //@ts-ignore
    mapDispatch,
  ),
)(ModalPrintSettings);
